import isEqual from 'lodash/isEqual'
import noop from 'lodash/noop'
import React, {
  FC,
  MutableRefObject,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { evaluatePosition } from 'features/ai/position-evaluation'
import { BoardState, GamePhase, Hand, PlayerColor, initialBoardState } from 'features/board/board'
import { oppositePlayer } from 'features/board/utils'
import { subscribeToGame } from 'features/firebase/game-operations'
import { Game, GameEvent, initialGameState } from 'features/firebase/types'
import { computeStateFromEvents } from 'features/game/compute-state-from-events'
import { HexBoard, initialHexBoard } from 'features/hex-board/hex-board'
import { useSoundContext } from 'features/sound/sound-context'
import { useAuthenticatedUser } from 'features/user/auth-store'

import { useStateReducer } from 'shared/hooks/use-state-reducer'

import { useGameClock } from './use-game-clock'
import { playEventSound } from './utils'

interface PlayerPositionData<T> {
  top: T
  bottom: T
}

export type RelativePlayerPosition = 'top' | 'bottom'

interface RelativeGameData {
  hand: PlayerPositionData<Hand>
  turn: PlayerPositionData<boolean>
  playerColor: PlayerPositionData<PlayerColor>
  playerId: PlayerPositionData<string | null>
  clock: PlayerPositionData<number>
}

export interface GameContextInterface {
  game: Game
  turn: PlayerColor
  hexBoard: HexBoard
  phase: GamePhase
  relativeGameData: RelativeGameData
  turnDuration: number
  boardState: BoardState
  resetTurn: AnyFunction
  lastMove: GameEvent | null
  updateBoardState: MutableRefObject<AnyFunction>
  positionScore: number
  prevMove: AnyFunction
  replayEvent: number | null
  nextMove: AnyFunction
}

export const initialGameContext: GameContextInterface = {
  game: initialGameState,
  turn: 'white',
  hexBoard: initialHexBoard,
  phase: 'distribute',
  turnDuration: 0,
  boardState: initialBoardState,
  relativeGameData: {
    hand: { top: [], bottom: [] },
    turn: { top: false, bottom: true },
    playerColor: { top: 'black', bottom: 'white' },
    playerId: { top: null, bottom: null },
    clock: { top: initialGameState.timeToPlay, bottom: initialGameState.timeToPlay },
  },
  resetTurn: noop,
  lastMove: null,
  updateBoardState: { current: noop },
  positionScore: 0,
  replayEvent: null,
  prevMove: noop,
  nextMove: noop,
}

export const GameContext = React.createContext<GameContextInterface>(initialGameContext)

export const GameProvider: FC<PropsWithChildren> = ({ children }) => {
  const navigate = useNavigate()
  const { user } = useAuthenticatedUser()
  const { gameId } = useParams()
  const [game, updateGame] = useStateReducer<Game>(initialGameState)
  const [boardState, setBoardState] = useState<BoardState>(initialBoardState)
  const { hexBoard, turn, hand, phase } = boardState
  const { clock, turnDuration, resetTurn } = useGameClock(game, turn)
  const prevGameRef = useRef<Game>(initialGameState)
  const [lastMove, setLastMove] = useState<GameEvent | null>(null)
  const [positionScore, setPositionScore] = useState(0)
  const [replayEvent, setReplayEvent] = useState<number | null>(null)

  useEffect(() => {
    if (game.status === 'finished') {
      setReplayEvent(game.events.length - 1)
    }
  }, [game.status, game.events])

  const prevMove = () => {
    if (replayEvent === null) return

    setReplayEvent(replayEvent > 0 ? replayEvent - 1 : 0)
  }

  const nextMove = () => {
    if (replayEvent === null) return

    setReplayEvent(replayEvent < game.events.length - 1 ? replayEvent + 1 : game.events.length - 1)
  }

  useEffect(() => {
    if (replayEvent === null) return

    const newState = computeStateFromEvents(game.events.slice(0, replayEvent + 1))
    if (!isEqual(newState, boardState)) {
      setBoardState(newState)
    }
  }, [boardState, game.events, replayEvent])

  const sounds = useSoundContext()
  const myPieces = game.players.white === user.uid ? 'white' : 'black'
  const relativeGameData: RelativeGameData = useMemo(
    () => ({
      hand: {
        top: hand[oppositePlayer(myPieces)],
        bottom: hand[myPieces],
      },
      turn: {
        top: turn === oppositePlayer(myPieces),
        bottom: turn === myPieces,
      },
      playerColor: {
        top: oppositePlayer(myPieces),
        bottom: myPieces,
      },
      playerId: {
        top: game.players[oppositePlayer(myPieces)],
        bottom: game.players[myPieces],
      },
      clock: {
        top: clock[oppositePlayer(myPieces)],
        bottom: clock[myPieces],
      },
    }),
    [myPieces, hand, turn, game.players, clock]
  )

  const _updateBoardState = useCallback(() => {
    const newState = computeStateFromEvents(game.events)
    if (!isEqual(newState, boardState)) {
      setBoardState(newState)
    }
  }, [game.events, boardState])

  const updateBoardState = useRef(_updateBoardState)

  useEffect(() => {
    updateBoardState.current = _updateBoardState
  }, [_updateBoardState])

  useEffect(() => {
    setPositionScore(evaluatePosition(boardState))
  }, [boardState])

  useEffect(() => {
    const prevGame = prevGameRef.current

    if (prevGame.events.length !== game.events.length) {
      const newEvents = game.events.slice(prevGame.events.length)
      if (newEvents.length > 0) {
        const newLastMove = newEvents[newEvents.length - 1]
        playEventSound(newLastMove.type, sounds)
        setLastMove(newLastMove)

        if (!['move', 'switch', 'withdraw', 'deploy', 'distribute', 'fight'].includes(newLastMove.type)) {
          updateBoardState.current()
        }
      }
    }

    prevGameRef.current = game
  }, [game, updateBoardState, sounds])

  useEffect(() => {
    if (!gameId) return

    subscribeToGame(gameId, (gameData: Game) => {
      if (!gameData) {
        navigate('/')
        return
      }

      const events = gameData.events ? Object.values(gameData.events) : []
      updateGame({ ...gameData, events })
    })
  }, [gameId, updateGame, navigate])

  return (
    <GameContext.Provider
      value={{
        game,
        boardState,
        turn,
        hexBoard,
        phase,
        replayEvent,
        relativeGameData,
        positionScore,
        turnDuration,
        lastMove,
        updateBoardState,
        resetTurn,
        prevMove,
        nextMove,
      }}
    >
      {children}
    </GameContext.Provider>
  )
}

export const useGameContext = () => useContext(GameContext)
